import { useQuery } from "@tanstack/react-query";
import { getIsoDateNow, startOfIsoDay, subDaysIso } from "helpers";
import { createQueryKey, QUERY_KEYS } from "helpers/useQuery/queryKeys";
import { ISOstring } from "model/modelTypes";
import React, { createContext, useContext, useState } from "react";
import { useAccountId, useOrganizationId } from "redux/auth";
import { useChangeHistoryService } from "./ChangeHistoryService";
import { ChangeHistoryRow } from "./types";

type ChangeHistoryContextType = {
  attributeFilter: string[];
  setAttributeFilter: (values: string[]) => void;
  entityFilter: string[];
  setEntityFilter: (values: string[]) => void;
  changedByFilter: string[];
  setChangedByFilter: (values: string[]) => void;
  data: ChangeHistoryRow[];
  loading: boolean;
  error: Error | null;
  dateRange: [ISOstring | null, ISOstring | null];
  setDateRange: (range: [ISOstring | null, ISOstring | null]) => void;
  campaignId?: string;
  lineitemId?: string;
  creativeId?: string;
};

const ChangeHistoryContext = createContext<
  ChangeHistoryContextType | undefined
>(undefined);

export const useChangeHistoryContext = () => {
  const context = useContext(ChangeHistoryContext);
  if (!context) {
    throw new Error(
      "useChangeHistoryContext must be used within ChangeHistoryProvider"
    );
  }
  return context;
};

const getDefaultDateRange = ({
  campaignId,
  lineitemId,
  creativeId,
}: {
  campaignId?: string;
  lineitemId?: string;
  creativeId?: string;
}): [ISOstring | null, ISOstring | null] => {
  if (lineitemId || campaignId || creativeId) {
    return [null, null];
  }

  const endDate = getIsoDateNow();
  const startDate = startOfIsoDay(subDaysIso(endDate, 30));
  return [startDate, endDate];
};

export const ChangeHistoryProvider = ({
  children,
  campaignId,
  lineitemId,
  creativeId,
  fieldPathPatterns,
}: {
  children: React.ReactNode;
  campaignId?: string;
  lineitemId?: string;
  creativeId?: string;
  fieldPathPatterns?: string[];
}) => {
  const [attributeFilter, setAttributeFilter] = useState<string[]>([]);
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [changedByFilter, setChangedByFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [ISOstring | null, ISOstring | null]
  >(getDefaultDateRange({ campaignId, lineitemId, creativeId }));

  const organizationId = useOrganizationId();
  const accountId = useAccountId();

  const { fetchHistoryData } = useChangeHistoryService();

  const {
    data = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: createQueryKey(
      QUERY_KEYS.CHANGE_HISTORY,
      organizationId,
      accountId,
      dateRange[0],
      dateRange[1],
      campaignId,
      lineitemId,
      creativeId,
      fieldPathPatterns
    ),
    queryFn: () => {
      return fetchHistoryData(
        dateRange[0],
        dateRange[1],
        campaignId,
        lineitemId,
        creativeId,
        fieldPathPatterns
      );
    },
    enabled:
      !lineitemId && !campaignId && !creativeId
        ? !!dateRange[0] && !!dateRange[1]
        : true,
    gcTime: 5 * 60 * 1000,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });

  return (
    <ChangeHistoryContext.Provider
      value={{
        attributeFilter,
        setAttributeFilter,
        entityFilter,
        setEntityFilter,
        changedByFilter,
        setChangedByFilter,
        data,
        loading,
        error: error as Error | null,
        dateRange,
        setDateRange,
        campaignId,
        lineitemId,
        creativeId,
      }}>
      {children}
    </ChangeHistoryContext.Provider>
  );
};
